/**
 * User Profile Page
 * Displays a user's profile information, posts, and follow actions.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthenticationContext';
import SinglePostDisplay from '../components/post/SinglePostDisplay';
import EditUserProfileForm from '../components/user/EditUserProfileForm';
import UserListModal from '../components/user/UserListModal';
import VerifiedBadge from '../components/common/VerifiedBadge';
import { computeWritingStreak } from '../utils/computeWritingStreak';
import { computeBadges } from '../utils/computeBadges';
import './UserProfilePage.css';

const UserProfilePage = () => {
  const { id } = useParams();
  const { user: currentUser, token, setUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFollowing, setIsFollowing] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);
  const [listModal, setListModal] = useState(null); // 'followers' | 'following' | null
  const [activeTab, setActiveTab] = useState('posts'); // 'posts' | 'likes'
  const [likedPosts, setLikedPosts] = useState([]);
  const [likedLoaded, setLikedLoaded] = useState(false);
  const [editForm, setEditForm] = useState({
    fullName: '',
    bio: '',
    avatar: ''
  });

  const fetchProfile = useCallback(async () => {
    try {
      const response = await axios.get(`/api/users/${id}`);
      setProfile(response.data.user);
      setPosts(response.data.posts);
      if (currentUser?.id) {
        setIsFollowing(
          response.data.user.followers?.some(
            f => {
              const followerId = f._id || f;
              return followerId?.toString() === currentUser?.id?.toString();
            }
          ) || false
        );
      }
      setEditForm({
        fullName: response.data.user.fullName || '',
        bio: response.data.user.bio || '',
        avatar: response.data.user.avatar || ''
      });
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  }, [id, currentUser?.id]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const handleFollow = async () => {
    if (!token) {
      window.location.href = '/login';
      return;
    }
    try {
      if (isFollowing) {
        await axios.post(`/api/users/${id}/unfollow`);
      } else {
        await axios.post(`/api/users/${id}/follow`);
      }
      setIsFollowing(!isFollowing);
      fetchProfile();
    } catch (error) {
      console.error('Error following/unfollowing:', error);
    }
  };

  const startEditing = () => {
    setSaveStatus(null);
    setIsEditing(true);
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    setSaveStatus(null);
    try {
      await axios.put(`/api/users/${id}`, editForm);
      await fetchProfile();
      // Reflect avatar/name changes in the navbar right away.
      if (currentUser?.id === id && setUser) {
        setUser(u => (u ? { ...u, avatar: editForm.avatar, fullName: editForm.fullName } : u));
      }
      setIsEditing(false);
      setSaveStatus({ type: 'success', text: 'Profile updated' });
    } catch (error) {
      setSaveStatus({
        type: 'error',
        text: error.response?.data?.message || 'Could not save changes. Please try again.'
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="container loading">Loading profile...</div>;
  }

  if (!profile) {
    return <div className="container">User not found</div>;
  }

  const isOwnProfile = currentUser?.id === id;
  const pinnedId = profile.pinnedPost ? (profile.pinnedPost._id || profile.pinnedPost).toString() : null;
  const isBlocked = currentUser?.blocked?.some(b => (b._id || b)?.toString() === id?.toString()) || false;
  const streak = computeWritingStreak(posts);
  const ownPosts = posts.filter(p => !p.repostOf);
  const badges = computeBadges({
    posts: ownPosts.length,
    likesReceived: ownPosts.reduce((sum, p) => sum + (p.likes?.length || 0), 0),
    followers: profile.followers?.length || 0,
    streak,
    accountAgeDays: profile.createdAt
      ? Math.floor((Date.now() - new Date(profile.createdAt)) / 86400000)
      : 0,
    isVerified: profile.isVerified
  });

  const handleBlock = async () => {
    try {
      const res = await axios.post(`/api/users/${id}/block`);
      if (setUser) setUser(u => (u ? { ...u, blocked: res.data.blockedList } : u));
      fetchProfile();
    } catch (error) {
      console.error('Error blocking user:', error);
    }
  };

  const fetchLikes = async () => {
    try {
      const res = await axios.get(`/api/users/${id}/likes`);
      setLikedPosts(res.data);
      setLikedLoaded(true);
    } catch (error) {
      console.error('Error fetching liked posts:', error);
    }
  };

  const showLikesTab = () => {
    setActiveTab('likes');
    if (!likedLoaded) fetchLikes();
  };

  const handlePinToggle = async (postId) => {
    try {
      await axios.post(`/api/posts/${postId}/pin`);
      fetchProfile();
    } catch (error) {
      console.error('Error pinning post:', error);
    }
  };

  // Show the pinned post first.
  const orderedPosts = pinnedId
    ? [...posts].sort((a, b) => (b._id === pinnedId ? 1 : 0) - (a._id === pinnedId ? 1 : 0))
    : posts;

  return (
    <div className="container">
      <div className="profile-header">
        <div className="profile-avatar-section">
          {profile.avatar ? (
            <img src={profile.avatar} alt={profile.username} className="profile-avatar-large" />
          ) : (
            <div className="profile-avatar-large profile-avatar-placeholder">
              {profile.username?.charAt(0).toUpperCase()}
            </div>
          )}
        </div>
        <div className="profile-info">
          <div className="profile-name-section">
            <h1>
              {profile.fullName || profile.username}
              {profile.isVerified && <VerifiedBadge size={22} />}
            </h1>
            <p className="profile-username">@{profile.username}</p>
          </div>
          {isOwnProfile && token ? (
            <button
              className="btn-edit-profile"
              onClick={() => isEditing ? handleSaveProfile() : startEditing()}
              disabled={saving}
            >
              {isEditing ? (saving ? 'Saving...' : 'Save') : 'Edit Profile'}
            </button>
          ) : token && !isOwnProfile ? (
            <div className="profile-actions">
              {!isBlocked && (
                <button
                  className={`btn-follow ${isFollowing ? 'following' : ''}`}
                  onClick={handleFollow}
                >
                  {isFollowing ? 'Following' : 'Follow'}
                </button>
              )}
              <button className="btn-block" onClick={handleBlock}>
                {isBlocked ? 'Unblock' : 'Block'}
              </button>
            </div>
          ) : (
            <Link to="/login" className="btn-edit-profile">
              Login to Follow
            </Link>
          )}
        </div>
      </div>

      {!isEditing && saveStatus && (
        <div className={`save-status ${saveStatus.type}`}>{saveStatus.text}</div>
      )}

      {isEditing && isOwnProfile && token ? (
        <EditUserProfileForm
          editForm={editForm}
          onFormChange={setEditForm}
          onSave={handleSaveProfile}
          onCancel={() => { setSaveStatus(null); setIsEditing(false); }}
          saving={saving}
          status={saveStatus}
        />
      ) : (
        profile.bio && (
          <div className="profile-bio">
            <p>{profile.bio}</p>
          </div>
        )
      )}

      <div className="profile-stats">
        <div className="stat">
          <span className="stat-number">{posts.length}</span>
          <span className="stat-label">Posts</span>
        </div>
        <button type="button" className="stat stat-button" onClick={() => setListModal('followers')}>
          <span className="stat-number">{profile.followers?.length || 0}</span>
          <span className="stat-label">Followers</span>
        </button>
        <button type="button" className="stat stat-button" onClick={() => setListModal('following')}>
          <span className="stat-number">{profile.following?.length || 0}</span>
          <span className="stat-label">Following</span>
        </button>
        {streak > 0 && (
          <div className="stat">
            <span className="stat-number">🔥 {streak}</span>
            <span className="stat-label">Day streak</span>
          </div>
        )}
      </div>

      {badges.length > 0 && (
        <div className="profile-badges">
          {badges.map((b) => (
            <span key={b.key} className="badge-chip" title={b.title}>
              <span className="badge-emoji">{b.emoji}</span> {b.label}
            </span>
          ))}
        </div>
      )}

      {listModal && (
        <UserListModal
          title={listModal === 'followers' ? 'Followers' : 'Following'}
          users={listModal === 'followers' ? profile.followers : profile.following}
          onClose={() => setListModal(null)}
        />
      )}

      <div className="profile-posts-section">
        <div className="feed-tabs">
          <button
            className={`feed-tab ${activeTab === 'posts' ? 'active' : ''}`}
            onClick={() => setActiveTab('posts')}
          >
            Posts
          </button>
          <button
            className={`feed-tab ${activeTab === 'likes' ? 'active' : ''}`}
            onClick={showLikesTab}
          >
            Likes
          </button>
        </div>

        {isBlocked ? (
          <div className="no-posts">
            <p>You have blocked this user. Unblock to see their posts.</p>
          </div>
        ) : activeTab === 'posts' ? (
          posts.length === 0 ? (
            <div className="no-posts"><p>No posts yet.</p></div>
          ) : (
            <div className="posts-list">
              {orderedPosts.map((post) => (
                <SinglePostDisplay
                  key={post._id}
                  post={post}
                  onUpdate={fetchProfile}
                  canPin={isOwnProfile && token && !post.repostOf}
                  isPinned={post._id === pinnedId}
                  onPinToggle={handlePinToggle}
                />
              ))}
            </div>
          )
        ) : (
          !likedLoaded ? (
            <div className="loading">Loading...</div>
          ) : likedPosts.length === 0 ? (
            <div className="no-posts"><p>No liked posts yet.</p></div>
          ) : (
            <div className="posts-list">
              {likedPosts.map((post) => (
                <SinglePostDisplay key={post._id} post={post} onUpdate={fetchLikes} />
              ))}
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default UserProfilePage;
